import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/contexts/auth-context'
import { signUpService } from '@/services/signup'

const EMAIL_REGEX = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i

export function SignupPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const emailRef = useRef<HTMLInputElement>(null)

  const [email, setEmail] = useState('')
  const [validEmail, setValidEmail] = useState(false)
  const [errMsg, setErrMsg] = useState('')
  const [errIsHtml, setErrIsHtml] = useState(false)
  const [formSuccess, setFormSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [website, setWebsite] = useState('')

  useEffect(() => {
    if (user) {
      navigate('/')
    } else {
      emailRef.current?.focus()
    }
  }, [user, navigate])

  useEffect(() => {
    setValidEmail(EMAIL_REGEX.test(email))
  }, [email])

  useEffect(() => {
    setErrMsg('')
    setErrIsHtml(false)
  }, [email])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const isValidEmail = EMAIL_REGEX.test(email)
    if (!isValidEmail) {
      setErrMsg('Please enter a valid email address.')
      return
    }

    // Honeypot: if the hidden field is filled, it's a bot — fake success
    if (website) {
      setFormSuccess(true)
      setEmail('')
      return
    }

    setIsLoading(true)
    try {
      await signUpService().getStarted(email)

      setFormSuccess(true)
      setEmail('')
    } catch (err: any) {
      console.error('Signup error:', err)
      if (err?.response?.status === 409) {
        setErrIsHtml(true)
        setErrMsg(`Email already exists. <a href="/login" class="font-medium text-primary underline underline-offset-4 hover:text-primary/80">Click here to sign in</a>`)
      } else if (err?.response?.data?.errors) {
        // Handle validation errors
        let errCompleteMsg: string[] = []
        Object.entries(err.response.data.errors).forEach(([, errArr]: [string, any]) => {
          if (Array.isArray(errArr)) {
            errCompleteMsg.push(errArr[1] || errArr[0] || 'Validation error')
          } else if (typeof errArr === 'string') {
            errCompleteMsg.push(errArr)
          }
        })
        setErrMsg(errCompleteMsg.join('<br />'))
      } else {
        setErrMsg(err?.response?.data?.message || 'Something went wrong. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (formSuccess) {
    return (
      <div className="container relative h-screen flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0">
        <Link
          to="/login"
          className="absolute right-4 top-4 md:right-8 md:top-8 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
        >
          Sign In
        </Link>
        <div className="relative hidden h-full flex-col bg-muted p-10 text-white lg:flex dark:border-r">
          <div className="absolute inset-0 bg-slate-700" />
          <div className="relative z-20 flex items-center text-lg font-medium">
            <img 
              src="/logo_white.png" 
              alt="Glaium Logo" 
              className="h-8 w-auto"
            />
          </div>
        </div>
        <div className="flex h-full items-center p-4 lg:p-8 bg-muted/40">
          <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
            <div className="flex flex-col space-y-2 text-center">
              <h1 className="text-2xl font-semibold tracking-tight">
                Check your email
              </h1>
              <p className="text-sm text-muted-foreground">
                We've sent a verification link to your email address. Please check your inbox and click the link to complete your account setup.
              </p>
            </div>
            <div className="grid gap-6">
              <Link to="/login">
                <Button className="w-full">
                  Back to Sign In
                </Button>
              </Link>
            </div>
            <p className="px-8 text-center text-sm text-muted-foreground">
              Didn't receive the email?{" "}
              <button 
                onClick={() => setFormSuccess(false)}
                className="underline underline-offset-4 hover:text-primary"
              >
                Try again
              </button>
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container relative h-screen flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      <Link
        to="/login"
        className="absolute right-4 top-4 md:right-8 md:top-8 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
      >
        Sign In
      </Link>
      <div className="relative hidden h-full flex-col bg-muted p-10 text-white lg:flex dark:border-r">
        <div className="absolute inset-0 bg-slate-700" />
        <div className="relative z-20 flex items-center text-lg font-medium">
          <img 
            src="/logo_white.png" 
            alt="Glaium Logo" 
            className="h-8 w-auto"
          />
        </div>
      </div>
      <div className="flex h-full items-center p-4 lg:p-8 bg-muted/40">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Create an account
            </h1>
            <p className="text-sm text-muted-foreground">
              Enter your email below to create your account
            </p>
          </div>
          <div className="grid gap-6">
            <form onSubmit={handleSubmit}>
              <div className="grid gap-2">
                {/* Honeypot field — hidden from humans, bots auto-fill it */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>
                <div className="grid gap-1">
                  <Label className="sr-only" htmlFor="email">
                    Email
                  </Label>
                  <Input
                    ref={emailRef}
                    id="email"
                    placeholder="name@example.com"
                    type="email"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect="off"
                    disabled={isLoading}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={email && !validEmail ? 'border-destructive' : ''}
                  />
                  {email && !validEmail && (
                    <p className="text-sm text-destructive">
                      Please enter a valid email address.
                    </p>
                  )}
                  {errMsg && (
                    errIsHtml ? (
                      <div
                        className="text-sm text-destructive"
                        dangerouslySetInnerHTML={{ __html: errMsg }}
                      />
                    ) : (
                      <p className="text-sm text-destructive">{errMsg}</p>
                    )
                  )}
                </div>
                <Button disabled={!validEmail || isLoading}>
                  {isLoading && (
                    <svg
                      className="mr-2 h-4 w-4 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  Sign Up with Email
                </Button>
              </div>
            </form>
          </div>
          <p className="px-8 text-center text-sm text-muted-foreground">
            By clicking continue, you agree to our{" "}
            <Link
              to="/terms"
              className="underline underline-offset-4 hover:text-primary"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy"
              className="underline underline-offset-4 hover:text-primary"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  )
}